import dynamic from 'next/dynamic';
import { useState, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux';
import { useServices } from '@/services/useServices';
import { selectAddressFormModalInfos } from '@/redux/slices/expro.slice';
import { ButtonModal } from '@/types';
import { STORE_ID } from '@/constants/stores';
import { URLS } from '@/constants/urls';
import { selectAddressSelectedStatus } from '@/redux/slices/checkout.slice';
import { endAssistantConversation } from '@/redux/slices/assistant.slice';

const ModalAlert = dynamic(() => import('@/components/layout/modals/ModalAlert'), { ssr: false });
ModalAlert.displayName = 'ModalAlert';
const ModalLogin = dynamic(() => import('@/components/layout/modals/ModalLogin'), { ssr: false });
ModalLogin.displayName = 'ModalLogin';
const ModalOutOfStock = dynamic(() => import('@/components/layout/modals/ModalOutOfStock'), { ssr: false });
ModalOutOfStock.displayName = 'ModalOutOfStock';
const ModalConfirm = dynamic(() => import('@/components/layout/modals/ModalConfirm'), { ssr: false });
ModalConfirm.displayName = 'ModalConfirm';
const ModalSelectGift = dynamic(() => import('@/components/layout/modals/ModalSelectGift'), { ssr: false });
ModalSelectGift.displayName = 'ModalSelectGift';
const ModalIncompatibleProducts = dynamic(() => import('@/components/layout/modals/ModalIncompatibleProducts'), { ssr: false });
ModalIncompatibleProducts.displayName = 'ModalIncompatibleProducts';
const ModalContact = dynamic(() => import('@/components/layout/modals/ModalContact'), { ssr: false });
ModalContact.displayName = 'ModalContact';
const RecommendationModal = dynamic(() => import('@/components/layout/modals/RecommendationModal'), { ssr: false });
RecommendationModal.displayName = 'RecommendationModal';
const ModalSelectDeliveryCountry = dynamic(() => import('@/components/layout/modals/ModalSelectDeliveryCountry'), { ssr: false });
ModalSelectDeliveryCountry.displayName = 'ModalSelectDeliveryCountry';
const ModalConfirmation = dynamic(() => import('@/components/layout/modals/ModalConfirmation'), { ssr: false });
ModalConfirmation.displayName = 'ModalConfirmation';
const LegalAgeModal = dynamic(() => import('@/components/layout/modals/LegalAgeModal'), { ssr: false });
LegalAgeModal.displayName = 'LegalAgeModal';
const AddressFormModal = dynamic(() => import('@/components/common/address/AddressFormModal'), { ssr: false });
AddressFormModal.displayName = 'AddressFormModal';
const AddProductToCartModal = dynamic(() => import('@/components/layout/modals/AddProductToCartModal'), { ssr: false });
AddProductToCartModal.displayName = 'AddProductToCartModal';
const MiniCartModal = dynamic(() => import('@/components/cart/mini-cart/MiniCart').then((mod) => mod.MiniCart), { ssr: false });
MiniCartModal.displayName = 'MiniCartModal';
const AddressErrorModal = dynamic(
  () => import('@/components/checkout/selectAddress/AddressErrorModal').then((mod) => mod.AddressErrorModal), { ssr: false }
);
AddressErrorModal.displayName = 'AddressErrorModal';
const VirtualAssistantModal = dynamic(() => import('@/components/virtual-assistant/virtual-assistant-modal')
  .then(((mod) => mod.VirtualAssistantModal)), { ssr: false });

export const AppModals = () => {
  const cart = useAppSelector(({ cart }) => cart?.content);
  const loginModalVisible = useAppSelector(({ gui }) => gui.loginModalVisible);
  const alertModalVisible = useAppSelector(({ gui }) => gui.alertModalVisible);
  const confirmModalVisible = useAppSelector(({ gui }) => gui.confirmModalVisible);
  const outOfStockModalVisible = useAppSelector(({ gui }) => gui.outOfStockModalVisible);
  const isSelectLanguageModalVisible = useAppSelector(({ gui }) => gui.isSelectLanguageModalVisible);
  const incompatibleProductsModalVisible = useAppSelector(({ gui }) => gui.incompatibleProductsModalVisible);
  const contactModalVisible = useAppSelector(({ gui }) => gui?.contactModalVisible);
  const recommendationModalVisible = useAppSelector(({ gui }) => gui.recommendationModalVisible);
  const confirmationModalVisible = useAppSelector(({ gui }) => gui.confirmationModalData.opened);
  const { addressToEdit, addressFormModalVisible, isFromExpro } = useAppSelector(selectAddressFormModalInfos);
  const addProductToCartModalVisible = useAppSelector(({ product }) => product.addProductToCartModalVisible);
  const storeId = useAppSelector(({ root }) => root.storeConf.storeId);
  const isMiniCartOpened = useAppSelector(({ cart }) => cart.isMiniCartOpened);
  const showAddressModalError = useAppSelector(selectAddressSelectedStatus);
  const isAssistantModalOpened = useAppSelector(({ assistant }) => assistant.modal.opened);

  const dispatch = useAppDispatch();

  const [gifts, setGifts] = useState<any>();

  useEffect(() => {
    if (cart?.id) {
      const gift = cart.gifts?.length ? cart.gifts : cart.giftPacks?.length ? cart.giftPacks : undefined;
      setGifts(gift);
    } else {
      setGifts(undefined);
    }
  }, [cart?.updateDate]);

  const isEditAddressForm = Object.keys(addressToEdit).length > 0;

  return (
    <>

      {showAddressModalError && (
        <AddressErrorModal opened={showAddressModalError} />
      )}

      {loginModalVisible && (
        <ModalLogin />
      )}

      {alertModalVisible && (
        <ModalAlert />
      )}

      {recommendationModalVisible && (
        <RecommendationModal isOpen={recommendationModalVisible}/>
      )}

      {confirmModalVisible && (
        <ModalConfirm />
      )}

      {outOfStockModalVisible && (
        <ModalOutOfStock />
      )}

      {incompatibleProductsModalVisible && (
        <ModalIncompatibleProducts />
      )}

      {gifts && (
        <ModalSelectGift gifts={gifts} />
      )}

      {contactModalVisible && (
        <ModalContact isOpen={contactModalVisible}/>
      )}

      {isSelectLanguageModalVisible && (
        <ModalSelectDeliveryCountry isOpen={isSelectLanguageModalVisible} />
      )}

      {confirmationModalVisible && (
        <ModalConfirmation />
      )}

      {storeId === STORE_ID.USA && (
        <LegalAgeModal />
      )}

      {addressFormModalVisible && (
        <AddressFormModal
          addressFromProps={addressToEdit}
          actionType={isEditAddressForm ? 'edit' : 'create'} isOpen={true}
          closeButton={{
            iconUrl: URLS.CROSS_BLACK
          } as ButtonModal}
          disableCountry={isFromExpro}
        />
      )}

      {addProductToCartModalVisible && (
        <AddProductToCartModal />
      )}

      {isMiniCartOpened &&
        <MiniCartModal />
      }

      {isAssistantModalOpened && (
        <VirtualAssistantModal
          isOpen={isAssistantModalOpened} closeModal={() => dispatch(endAssistantConversation())}
        />
      )}
    </>
  );
};

export default AppModals;
